import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {LabAutomationAllSchemasQuery} from 'lab-automation/graphql/__generated/LabAutomationAllSchemasQuery';
import {LAB_AUTOMATION_ALL_SCHEMAS_QUERY} from 'lab-automation/graphql/AllSchemas';
import {LookupConfigContext} from 'lab-automation/settings/automation-input-file-config/LookupConfigContext';
import {LookupConfigSpec, LookupStepInfo, LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {canFieldBePlate, getStepItemWithSourceDestinationType} from 'lookup-config/LookupConfigUtils';
import {LookupSchemaSurface, useReportLookupSchemasReadyOnce} from 'lookup-config/lookupSchemaRum';
import {lookupStepsToInfosArray} from 'lookup-config/LookupStepInitializerUtils';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {LookupConfigTypedValue, PlateLayoutTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {Schema} from 'schemas/SchemaConstants';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {PlateLayoutType, readablePlateLayout, stringToPlateLayout} from '../RunSchemaSpecConstants';

export interface PlateLayoutCellDropdownProps {
  isSelected: boolean;
  typedValue: PlateLayoutTypedValue;
  onOpenDropdown: () => void;
  onUpdate: (value: PlateLayoutTypedValue) => void;
}

interface OptionValue {
  value: string;
  disabled: boolean;
}

const _toLookupConfigSpec = (lookup: LookupConfigTypedValue | null): LookupConfigSpec | null =>
  lookup != null ? (lookup as LookupConfigSpec) : null;

function isSourceOrDestinationPlateLookup(
  lookup: LookupStepInfo & {lookupStepContext: Array<LookupStepInfo>}
): boolean {
  const lastLookup = getStepItemWithSourceDestinationType(lookup);
  if (!lastLookup) {
    return false;
  }
  if (lastLookup.lookupStep.type === LookupStepType.SCHEMA_FIELD) {
    return lastLookup.schemaField != null && canFieldBePlate(lastLookup.schemaField);
  }
  return (
    lastLookup.lookupStep.type === LookupStepType.PLATE ||
    lastLookup.lookupStep.type === LookupStepType.PLACEHOLDER_PLATES
  );
}

function isLookupConfigPlateLookup(lookupConfig: LookupConfigSpec, parentSchema: Schema | null): boolean {
  const dummySourceStep: LookupStepInfo & {lookupStepContext: Array<LookupStepInfo>} = {
    name: 'DUMMY_SOURCE',
    lookupStep: {type: LookupStepType.SOURCE},
    lookupStepContext: lookupStepsToInfosArray(lookupConfig.lookupSteps, parentSchema, {}),
    parentSchema,
    linkSchema: null,
  };

  return isSourceOrDestinationPlateLookup(dummySourceStep);
}

export function canApplyPlateLayout(
  source: LookupConfigTypedValue,
  destination: LookupConfigTypedValue | null,
  runSchema: Schema | null
): boolean {
  const sourceLookup = _toLookupConfigSpec(source);
  const destinationLookup = _toLookupConfigSpec(destination);

  if (sourceLookup == null || destinationLookup == null) {
    return false;
  }
  if (!isLookupConfigPlateLookup(sourceLookup, runSchema)) {
    return false;
  }
  if (!isLookupConfigPlateLookup(destinationLookup, runSchema)) {
    return false;
  }
  return true;
}

export default function PlateLayoutCellDropdown(props: PlateLayoutCellDropdownProps): JSX.Element {
  const {baseSchema} = LookupConfigContext.useContext();
  const [isOpen, setOpen] = useState(false);
  useEffect(() => {
    if (!props.isSelected) {
      setOpen(false);
    }
  }, [props.isSelected]);

  // All schemas need to be loaded before we can call canApplyPlateLayout
  const allSchemasQueryResult = useBenchlingQuery<LabAutomationAllSchemasQuery>(
    LAB_AUTOMATION_ALL_SCHEMAS_QUERY
  );
  useReportLookupSchemasReadyOnce(
    LookupSchemaSurface.LAB_AUTOMATION_INPUT_FILE_CONFIG,
    allSchemasQueryResult.type === QueryResultType.SUCCESS
  );
  if (allSchemasQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  const _handleOpenMenu = (): void => {
    props.onOpenDropdown();
    setOpen(true);
  };
  const canUsePlateLayout = canApplyPlateLayout(
    props.typedValue.source,
    props.typedValue.destination,
    baseSchema
  );
  const options: Array<OptionValue> = [{value: 'None', disabled: false}];
  options.push(
    ...Object.values(PlateLayoutType).map((plateLayoutType) => ({
      value: readablePlateLayout(plateLayoutType),
      disabled: !canUsePlateLayout,
    }))
  );

  return (
    <div className="u-pointer u-flex-display" onClick={_handleOpenMenu}>
      <span className="u-flex-grow">{formatTypedValue(props.typedValue)}</span>
      <div className="selectorDropdown">
        <AbsoluteDropdownContainer
          onRequestClose={() => {
            setOpen(false);
          }}
          open={isOpen}
          shouldCloseOnInsideClick={true}
          menu={
            <ul className="selectorDropdown-list dropdown-menu scrollable pull-right">
              {options.map(({value, disabled}, i) => (
                <Tooltip
                  key={`option-${i}`}
                  placement={TooltipPlacement.LEFT}
                  title={
                    disabled
                      ? 'The source and destination for this row must look up a plate to use a plate layout'
                      : ''
                  }
                >
                  <li className={classNames({disabled})}>
                    <a
                      onClick={() => {
                        if (!disabled) {
                          props.onUpdate({
                            ...props.typedValue,
                            plateLayout: value !== 'None' ? stringToPlateLayout(value) : null,
                          });
                        }
                      }}
                    >
                      {value}
                    </a>
                  </li>
                </Tooltip>
              ))}
            </ul>
          }
        >
          <BenchlingIcon
            className={classNames('selectorDropdown-button', {'is-open': isOpen})}
            icon="chevron-down"
            onClick={_handleOpenMenu}
          />
        </AbsoluteDropdownContainer>
      </div>
    </div>
  );
}
